import { useEffect, useState } from 'react';
import { axiosInstance, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Eye, Search } from 'lucide-react';
import { toast } from 'sonner';

const InactiveClientsList = ({ user }) => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchClients();
  }, []);

  const fetchClients = async () => {
    try {
      const res = await axiosInstance.get(`${API}/clients/hidden`);
      setClients(res.data || []);
    } catch (e) {
      toast.error('Failed to load inactive managers');
    } finally {
      setLoading(false);
    }
  };

  const unhide = async (id) => {
    try {
      setBusyId(id);
      await axiosInstance.post(`${API}/clients/${id}/unhide`);
      toast.success('Manager restored to All Managers');
      await fetchClients();
    } catch (e) {
      toast.error('Failed to unhide manager');
    } finally {
      setBusyId(null);
    }
  };

  if (user.role !== 'admin') {
    return null;
  }

  if (loading) {
    return <div className="text-slate-600">Loading inactive managers...</div>;
  }

  const normalizedQuery = searchTerm.trim().toLowerCase();
  const filteredClients = clients.filter((client) =>
    (client.client_name || '').toLowerCase().includes(normalizedQuery)
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <h2 className="text-2xl font-bold text-slate-100" data-testid="inactive-managers-title">Inactive Managers</h2>
        <p className="text-sm text-slate-300" data-testid="inactive-managers-count">
          Showing {filteredClients.length} of {clients.length}
        </p>
      </div>

      <div className="relative max-w-lg">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search inactive managers by name"
          className="input-dark pl-10"
          data-testid="inactive-managers-search-input"
        />
      </div>

      {clients.length === 0 ? (
        <Card className="beast-card">
          <CardContent className="py-12 text-center text-slate-300" data-testid="inactive-managers-empty-state">
            No inactive managers
          </CardContent>
        </Card>
      ) : filteredClients.length === 0 ? (
        <Card className="beast-card">
          <CardContent className="py-12 text-center text-slate-300" data-testid="inactive-managers-no-search-results">
            No inactive managers match your search
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredClients.map((c) => (
            <Card key={c.id} className="beast-card" data-testid={`inactive-manager-card-${c.id}`}>
              <CardHeader>
                <CardTitle className="text-lg text-slate-100" data-testid={`inactive-manager-name-${c.id}`}>{c.client_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Button
                    onClick={() => unhide(c.id)}
                    className="bg-green-600 hover:bg-green-700"
                    disabled={busyId === c.id}
                    data-testid={`unhide-client-btn-${c.id}`}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    Unhide
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default InactiveClientsList;
